import { memo, useState } from "react";
import { twJoin } from "tailwind-merge";

import { useImageResolver } from "@/api/hooks/useImageResolver";
import { ImageModal } from "@/components/Carousel/Carousel";
import { XCloseIcon } from "@/components/Icons/XCloseIcon";
import { ZoomInIcon } from "@/components/Icons/ZoomInIcon";
import { type FormImage, isImageUploaded } from "@/components/ImageInput/useImageInput";
import { ImagePlaceholder } from "@/components/ImagePlaceholder/ImagePlaceholder";
import { buttonVariants } from "@/design-system/Button";
import { Tooltip } from "@/design-system/Tooltip";
import { preloadImage } from "@/helpers/image";
import { useBool } from "@/hooks/useBool";
import { useKey } from "@/hooks/useKey";
import { useTranslation } from "@/translations";
import { MediaLayoutGroup, MediaLayoutOverlay } from "@/ui-adapters/MediaLayoutMotion";
import { RuntimeAspectRatio } from "@/ui-adapters/RuntimeAspectRatio";

import { IconButton } from "../Button/IconButton";

export interface GalleryProps {
  images: FormImage[];
  isDeleted?: boolean;
  onRemove?: (image: FormImage) => void;
}
export const Gallery = memo(function Gallery({ images, isDeleted, onRemove }: GalleryProps) {
  const [isImageModalOpened, imageModalHandler] = useBool(false);
  const [zoomedImageIndex, setZoomedImageIndex] = useState<number | undefined>(undefined);
  const { t } = useTranslation();
  const resolveImage = useImageResolver();
  useKey("Escape", () => imageModalHandler.setFalse());
  if (images.length === 0) {
    return null;
  }

  return (
    <>
      <ul className={twJoin("flex flex-wrap items-end gap-space-sm", onRemove && "pt-space-md")}>
        <MediaLayoutGroup>
          {images.map((image, i) => (
            <li key={image.url} className={twJoin("relative", isDeleted && "opacity-30")}>
              <Tooltip tooltip={t("component.gallery.zoom-in-image")}>
                <button
                  type="button"
                  aria-label={t("component.gallery.zoom-in-image")}
                  className={twJoin(buttonVariants({ variant: "ghostSecondary", type: "button" }), "group")}
                  onClick={() => {
                    setZoomedImageIndex(i);
                    imageModalHandler.setTrue();
                  }}
                  onMouseEnter={() => {
                    if ("id" in image) {
                      preloadImage(image.url);
                    }
                  }}
                >
                  <MediaLayoutOverlay layoutId={image.url} />
                  <GalleryImage image={image} src={"id" in image ? resolveImage(image, "intrinsic") : image.url} />

                  <span className="absolute top-2 right-2 flex size-icon-display items-center justify-center rounded-full bg-background-default-inverse/40 text-text-default-inverse opacity-0 shadow-raised transition-opacity group-hover:opacity-100">
                    <ZoomInIcon size={20} />
                  </span>
                </button>
              </Tooltip>

              {onRemove && (
                <div className="absolute -top-4 -right-4">
                  <IconButton
                    icon={<XCloseIcon />}
                    onClick={(event: any) => {
                      event.stopPropagation();
                      onRemove(image);
                    }}
                    title={t("component.gallery.remove-image")}
                    size="sm"
                    isCircular
                  />
                </div>
              )}
            </li>
          ))}
        </MediaLayoutGroup>
      </ul>
      <ImageModal
        isOpened={isImageModalOpened}
        onOpenChange={imageModalHandler.toggle}
        images={images}
        zoomedImageIndex={zoomedImageIndex}
      />
    </>
  );
});

function GalleryImage({ image, src }: { image: FormImage; src: string }): React.ReactNode {
  const [isLoading, setIsLoading] = useState(true);
  const isUploaded = isImageUploaded(image);
  const imageWidth = isUploaded ? image.metadata.width : undefined;
  const imageHeight = isUploaded ? image.metadata.height : undefined;
  const aspectRatio = imageWidth && imageHeight ? imageWidth / imageHeight : undefined;

  return (
    <>
      {isUploaded && isLoading && <ImagePlaceholder image={image} objectFit="contain" />}
      <RuntimeAspectRatio aspectRatio={aspectRatio}>
        {(aspectRatioProps) => (
          <img
            {...aspectRatioProps}
            className="max-h-media-preview max-w-full rounded-sm bg-background-secondary shadow-surface select-none @sm:max-w-xs"
            loading="lazy"
            src={src}
            alt={isUploaded ? (image.description ?? "") : ""}
            onLoad={() => setIsLoading(false)}
            onError={() => setIsLoading(false)}
          />
        )}
      </RuntimeAspectRatio>
    </>
  );
}
